import i18n from '@/localisation/i18n';
import { mutations as rootMutations } from '@/store/mutations';
import { AppState } from '@/store/store';
import { ActionHandler } from '@/types/vuex.custom';
import { PermissionError } from '@/utils/errors/permission-error';
import { ClientConfig, LoginPreparePasswordResponse, UXConfig } from '@/utils/helpers/rest';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { SocketMessage, SocketNotification } from '@/utils/helpers/socket';
import axios, { AxiosError } from 'axios';

import {
  actions as manualLoansActions,
  mutations as manualLoansMutations,
} from '@/modules/manual-loan/store/store';
import { MarketTimeframes } from '@/modules/market-closed/types/market-closed';
import router from '@/router/router';
import { socketDebouncConfig, socketDebounceTime } from '@/store/store.const';
import { ApiError } from '@/utils/errors/api-error';
import { initSentry } from '@/utils/helpers/sentry';
import { debounce, each, noop } from 'lodash';
import { Store } from 'vuex';
import {
  Login2FAResponse,
  LoginResponse,
  RawLogin2FAResponse,
  RawLoginResponse,
} from '@/modules/common/models';
import { useCompaniesStore } from '@/store/companies-store';
import { useAgreementsStore } from '@/store/agreements-store';

// @TODO should move into a types d.ts for store related interfaces
type AllMutations = typeof rootMutations & typeof manualLoansMutations;
type StoreAction = ActionHandler<AppState, AppState, AllMutations>;
type AllActions = typeof actions & typeof socketActions & typeof manualLoansActions;

/**
 * Export key names as a helper type
 */
export type ActionKeys = keyof AllActions;

export const actions = {
  fetchClientConfig: async function (context) {
    try {
      const { data } = await axios.get<{ clientConfig: ClientConfig }>('/api/1/config');
      if (!data.clientConfig) {
        throw new ApiError('server not ready');
      }

      context.commit('updateClientConfig', data.clientConfig);

      // set extra defaults if in demo mode
      if (data.clientConfig.demoMode) {
        const uxConfig: UXConfig = { ...context.state.uxConfig };
        context.commit('updateUXConfig', uxConfig);
      }
      await context.dispatch('connectToSentry');
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError));
    }
  } as StoreAction,

  connectToSentry: function (context) {
    const cfg = context.state.clientConfig;
    if (!context.state.sentryConnected && cfg != null) {
      if (cfg.sentryServerDSN !== '') {
        initSentry(cfg);
        context.commit('SENTRY_CONNECT', true);
      }
    }
  } as StoreAction,

  logout: async function ({ commit, dispatch }) {
    commit('clearCurrentUser');
    await dispatch('cancelPendingSocketHandles');
  } as StoreAction,

  refreshCurrentUser: async function ({ dispatch }) {
    await dispatch('fetchCurrentUser');
  } as StoreAction,

  fetchCurrentUser: async function ({ commit }) {
    try {
      const { data } = await axios.get<RawLoginResponse>('/api/1/me');
      const response = LoginResponse.fromData(data);
      const user = response.user;

      await Promise.all([
        useAgreementsStore().refreshAgreements(),
        useCompaniesStore().refreshCompanies(),
      ]);

      commit('updateCurrentUser', user);
    } catch (err) {
      if (
        (err as AxiosError).response?.status == 401 ||
        (err as AxiosError).response?.status == 403
      ) {
        throw new PermissionError(i18nServerMessage(err as AxiosError));
      } else {
        throw new ApiError(i18nServerMessage(err as AxiosError));
      }
    }
  } as StoreAction,

  fetchCurrent2FA: async function ({ commit }) {
    try {
      const { data } = await axios.get('/api/1/me/settings/tfa');
      commit('update2FASettings', data.settings);
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError));
    }
  } as StoreAction,

  requestPasswordReset: async function (_context, loginEmail: string) {
    try {
      await axios.post('/api/1/login-lost-password', { email: loginEmail });
    } catch (e) {
      throw new ApiError(
        i18nServerMessage(
          e as AxiosError,
          i18n.t('loginResetPasswordEmailFailed', { emailAddress: loginEmail }) as string
        )
      );
    }
  } as StoreAction,

  changeLoginPassword: async function (
    _context,
    payload: { oldHashedPassword: string; hashedPassword: string }
  ) {
    try {
      await axios.post('/api/1/login-change-password', {
        oldPassword: payload.oldHashedPassword,
        newPassword: payload.hashedPassword,
      });
    } catch (e) {
      throw new ApiError(
        i18nServerMessage(e as AxiosError, i18n.t('login-password-not-available') as string)
      );
    }
  } as StoreAction,

  prepareNewLoginPassword: async function (
    _context,
    encSecret: string
  ): Promise<LoginPreparePasswordResponse> {
    try {
      const { data } = await axios.post('/api/1/login-prepare-password', {
        encSecret: encSecret,
      });
      return data;
    } catch (e) {
      throw new ApiError(
        i18nServerMessage(e as AxiosError, i18n.t('loginResetPasswordEmailExpired') as string)
      );
    }
  } as StoreAction,

  confirm2FALogin: async function (
    _context,
    payload: { email: string; tfaCode: string }
  ): Promise<Login2FAResponse> {
    try {
      const { data } = await axios.post<RawLogin2FAResponse>('/api/1/login-confirm-2fa', {
        email: payload.email,
        tfaCode: payload.tfaCode,
      });
      return Login2FAResponse.fromData(data);
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError, 'invalid2FACode'));
    }
  } as StoreAction,

  enable2FALogin: async function (_context, tfaCode: string) {
    try {
      await axios.put('/api/1/me/settings/tfa/enable', { tfaCode });
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError, 'enabled2FA.error'));
    }
  } as StoreAction,

  disable2FALogin: async function (_context, tfaCode: string) {
    try {
      await axios.put('/api/1/me/settings/tfa/disable', { tfaCode });
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError, 'disabled2FA.error'));
    }
  } as StoreAction,

  lost2FADevice: async function (_context, loginEmail: string) {
    try {
      await axios.post('/api/1/login-lost-device', { email: loginEmail });
    } catch (e) {
      throw new ApiError(
        i18nServerMessage(
          e as AxiosError,
          i18n.t('loginResetDeviceEmailFailed', { emailAddress: loginEmail }) as string
        )
      );
    }
  } as StoreAction,

  reset2FALogin: async function (_context, encSecret: string) {
    try {
      await axios.post('/api/1/login-reset-2fa', { encSecret: encSecret });
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError, 'invalidResetCode'));
    }
  } as StoreAction,
};

export const socketActions = {
  cancelPendingSocketHandles: function () {
    // find just the debounced socket actions and cancel them if they are pending
    each(socketActions, (action) => {
      if ('cancel' in action) {
        action.cancel();
      }
    });
  } as StoreAction,

  refreshClientConfig: debounce(
    function (this: Store<AppState>, { dispatch }, _sm: SocketMessage<SocketNotification>) {
      void dispatch('fetchClientConfig').catch(noop);
    } as StoreAction,
    socketDebounceTime,
    socketDebouncConfig
  ),

  refreshNotifications: function (_context, _msg) {
    // this socket event is handled by the useNotifications composable
    // we only implement the dummy handler here to avoid a Vuex warning
  } as StoreAction,

  refreshLenderLoan: function ({ commit }, msg: SocketMessage<string>) {
    commit('updateLenderLoanSocketEvent', msg.payload);
  } as StoreAction,

  refreshBorrowerLoan: function ({ commit }, msg: SocketMessage<string>) {
    commit('updateBorrowerLoanSocketEvent', msg.payload);
  } as StoreAction,

  refreshBorrowerOrders: debounce(
    function ({ dispatch }, _payload: SocketMessage<string>) {
      void dispatch('fetchBorrowerOrders').catch(noop);
    } as StoreAction,
    socketDebounceTime,
    socketDebouncConfig
  ),

  refreshLenderOrders: debounce(
    function ({ dispatch }, _payload: SocketMessage<string>) {
      void dispatch('fetchLenderOrders').catch(noop);
    } as StoreAction,
    socketDebounceTime,
    socketDebouncConfig
  ),

  refreshBrokerLoan: function ({ commit }, msg: SocketMessage<string>) {
    commit('updateBrokerLoanSocketEvent', msg.payload);
  } as StoreAction,

  // incoming socket event
  userDisabled: function (context, _payload: SocketMessage<string>) {
    context.commit('clearCurrentUser');
    // @TODO Router is not installed on the instance of Vue we have under this._vm
    // we should inject the router by another means
    // void this._vm?.$router.push({ name: 'home' }).catch(noop);
    void router.push({ name: 'home' }).catch(noop);
  } as StoreAction,

  // incoming socket event
  refreshMarketTimeframes: function (context, message: SocketMessage<MarketTimeframes>) {
    context.commit('updateMarketTimeframes', message.payload);
  } as StoreAction,

  // incoming socket event
  refreshCompanies: function (_context) {
    // No-op: Components now fetch companies locally instead of using Vuex state.
    // Socket events are still received but no longer trigger Vuex updates.
  } as StoreAction,

  // incoming socket event
  refreshUsers: function (_context) {
    // No-op: All admin components (broker-admin and trader-admin) now fetch users locally
    // instead of using Vuex state. Socket events are still received but no longer trigger
    // Vuex updates.
  } as StoreAction,

  // incoming socket event
  refreshFrontendHash: function (context, message: SocketMessage<string>) {
    context.commit('updateFrontendHash', message.payload);
  } as StoreAction,

  refreshOmsOrders: function ({ commit }, msg: SocketMessage<{ orderRef: string }>) {
    commit('updateMarketplaceOrdersEvent', msg.payload.orderRef);
  } as StoreAction,

  refreshAgreements: function (_context, _msg: SocketMessage<object>) {
    // this socket event is handled by a pinia store
    // we only implement the dummy handler here to avoid a Vuex warning
  } as StoreAction,

  refreshOmsUnackedExecutions: function (_context, _msg: SocketMessage<{ unackedCount: number }>) {
    // this socket event is handled by a pinia store
    // we only implement the dummy handler here to avoid a Vuex warning
  } as StoreAction,

  refreshVenueOrders: function (_context, _msg: SocketMessage<object>) {
    // this socket event is handled by a pinia store
    // we only implement the dummy handler here to avoid a Vuex warning
  } as StoreAction,

  refreshVenueTopOfBook: function (_context, _msg: SocketMessage<object>) {
    // this socket event is handled by a pinia store
    // we only implement the dummy handler here to avoid a Vuex warning
  } as StoreAction,

  termLoanChanged: function ({ commit }, msg: SocketMessage<string>) {
    commit('updateTermLoansEvent', msg.payload);
  } as StoreAction,

  refreshUIConfigs: function (_context, _msg: SocketMessage<string>) {
    // this socket event is handled by a pinia store
    // we only implement the dummy handler here to avoid a Vuex warning
  } as StoreAction,

  refreshInstrumentPrice: function (_context, _msg: SocketMessage<object>) {
    // this socket event is handled by a pinia store
    // we only implement the dummy handler here to avoid a Vuex warning
  } as StoreAction,
};
